import React, { useState, useRef, useCallback } from "react";
import { Check, Upload, X } from "lucide-react";

interface FileUploadProps {
  title: string;
  description?: string;
  onFileUploaded: (base64: string, filename: string) => void;
  onFileRemoved?: () => void;
  acceptedTypes?: string;
  uploadText?: string;
  maxSizeMB?: number;
  currentFile?: { name: string; base64: string } | null;
  disabled?: boolean;
  showPreview?: boolean;
  previewSize?: "small" | "medium" | "large";
}

const validateFile = (
  file: File,
  acceptedTypes: string,
  maxSizeMB: number,
): { valid: boolean; error?: string } => {
  const validTypes = acceptedTypes.split(",").map((type) => type.trim());
  const maxSize = maxSizeMB * 1024 * 1024;

  if (!validTypes.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      error: `Please upload a valid file type: ${acceptedTypes.replace(/image\//g, "").toUpperCase()}`,
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size must be less than ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
};

const processImage = (
  file: File,
  maxSize = 1024,
  quality = 0.8,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Only resize if image is larger than maxSize - avoid upscaling
      const scale = Math.min(
        1,
        Math.min(maxSize / img.width, maxSize / img.height),
      );
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;

      // Set canvas to actual scaled dimensions (not forced square)
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;

      if (ctx) {
        // For images with transparency, fill with white background
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, scaledWidth, scaledHeight);

        // Draw the scaled image
        ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);

        // Convert to base64 with configurable quality
        const base64 = canvas.toDataURL("image/jpeg", quality);
        resolve(base64);
      } else {
        reject(new Error("Could not get canvas context"));
      }

      // Clean up object URL
      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };

    img.src = URL.createObjectURL(file);
  });
};

const FileUpload: React.FC<FileUploadProps> = ({
  title,
  description,
  uploadText,
  onFileUploaded,
  onFileRemoved,
  acceptedTypes = "image/jpeg,image/jpg,image/png,image/heic,image/heif",
  maxSizeMB = 10,
  currentFile = null,
  disabled = false,
  showPreview = false,
  previewSize = "medium",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setError(null);
  };

  // Helper function to get preview image classes based on size
  const getPreviewClasses = () => {
    const sizeClasses = {
      small: "tw-w-12 tw-h-12",
      medium: "tw-w-16 tw-h-16",
      large: "tw-w-24 tw-h-24",
    };

    return `tw-mx-auto tw-rounded tw-object-contain ${sizeClasses[previewSize]}`;
  };

  const getUploadAreaClasses = () => {
    const baseClasses = `
      tw-relative tw-border-2 tw-border-dashed tw-rounded-lg tw-p-4 tw-text-center tw-cursor-pointer
      tw-transition-all tw-duration-200 tw-min-h-[120px] tw-flex tw-flex-col tw-items-center tw-justify-center
      tw-bg-neutral-800/5 tw-border-neutral-800/20 hover:tw-border-neutral-800/40
    `;

    if (disabled) {
      return `${baseClasses} tw-border-neutral-200 tw-cursor-not-allowed`;
    }

    if (isDragging) {
      return `${baseClasses} tw-border-neutral-400`;
    }

    return `${baseClasses} tw-border-neutral-300 hover:tw-border-neutral-500`;
  };

  const renderProcessingState = () => (
    <div className="tw-space-y-2">
      <div className="tw-w-8 tw-h-8 tw-mx-auto tw-border-4 tw-border-neutral-200 tw-border-t-neutral-600 tw-rounded-full tw-animate-spin"></div>
      <p className="tw-text-sm tw-text-neutral-600">Processing file...</p>
    </div>
  );

  const renderFilePreview = () => {
    if (showPreview && currentFile?.base64.startsWith("data:image")) {
      return (
        <img
          src={currentFile.base64}
          alt="Preview"
          className={getPreviewClasses()}
        />
      );
    }

    return (
      <div className="tw-w-6 tw-h-6 tw-mx-auto tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
        <Check className="tw-w-3 tw-h-3 tw-text-green-600" />
      </div>
    );
  };

  const renderUploadedState = () => (
    <div className="tw-space-y-2">
      <div className="tw-relative tw-inline-block">
        {renderFilePreview()}
        {!disabled && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            className="tw-absolute tw-top-0 tw--right-2 tw-w-4 tw-h-4 tw-bg-neutral-500 hover:tw-bg-neutral-600 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors tw-shadow-sm"
            title="Remove file"
          >
            <X className="tw-w-3 tw-h-3" />
          </button>
        )}
      </div>
      <div>
        <p className="tw-text-sm tw-font-medium tw-text-neutral-800">
          File uploaded successfully!
        </p>
        <p className="tw-text-xs tw-text-neutral-600 tw-mt-1">
          {currentFile?.name}
        </p>
      </div>
    </div>
  );

  const renderUploadPrompt = () => (
    <div className="tw-space-y-2">
      <div className="tw-w-6 tw-h-6 tw-mx-auto tw-bg-neutral-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
        <Upload className="tw-w-3 tw-h-3 tw-text-neutral-400" />
      </div>
      <div>
        <p className="tw-text-sm tw-text-neutral-600">
          {uploadText || "Click to upload"}
        </p>
        <p className="tw-text-xs tw-text-neutral-500 tw-mt-1">
          {acceptedTypes.replace(/image\//g, "").toUpperCase()} files up to{" "}
          {maxSizeMB}MB
        </p>
      </div>
    </div>
  );

  const renderUploadContent = () => {
    if (isProcessing) {
      return renderProcessingState();
    }

    if (currentFile) {
      return renderUploadedState();
    }

    return renderUploadPrompt();
  };

  const handleFile = useCallback(
    async (file: File) => {
      if (disabled) return;

      resetState();
      setIsProcessing(true);

      try {
        // Validate file
        const validation = validateFile(file, acceptedTypes, maxSizeMB);
        if (!validation.valid) {
          setError(validation.error || "Invalid file");
          return;
        }

        // Process image (resize, compress, convert to JPEG)
        const processedBase64 = await processImage(file);

        // Generate processed filename with .jpg extension
        const processedFilename =
          file.name.replace(/\.[^/.]+$/, "") + "_processed.jpg";

        onFileUploaded(processedBase64, processedFilename);
      } catch (error) {
        console.error("File processing failed:", error);
        setError("Failed to process image. Please try again.");
      } finally {
        setIsProcessing(false);
      }
    },
    [disabled, acceptedTypes, maxSizeMB, onFileUploaded],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile],
  );

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = () => {
    if (!disabled) {
      onFileUploaded("", "");
      onFileRemoved?.();
      resetState();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="tw-space-y-3">
      <h3 className="tw-text-base tw-font-medium tw-text-neutral-800">
        {title}
      </h3>

      {description && (
        <p className="tw-text-xs tw-text-neutral-600">{description}</p>
      )}

      {/* Upload area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleClick();
          }
        }}
        role="button"
        tabIndex={0}
        className={getUploadAreaClasses()}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          accept={acceptedTypes}
          className="tw-hidden"
          disabled={disabled}
        />

        {renderUploadContent()}
      </div>

      {/* Error message */}
      {error && (
        <div className="tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg">
          <p className="tw-text-sm tw-text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
